'use client';

import { getCopy } from '@/lib/copy';

const R = '/_auto/components/Logo';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg'
  showText?: boolean
  animated?: boolean
  variant?: 'default' | 'inverted'
}

const sizePx: Record<NonNullable<LogoProps['size']>, number> = {
  sm: 32,
  md: 40,
  lg: 96,
};

const textSizes: Record<NonNullable<LogoProps['size']>, string> = {
  sm: 'text-lg',
  md: 'text-2xl',
  lg: 'text-4xl',
};

/**
 * Spark Envelope brand mark (issue #222).
 * Mirrors packages/frontend/public/brand/icon.svg (default) and
 * icon-inverted.svg (inverted). SVG gradient IDs are namespaced per
 * variant so multiple instances on one page don't collide.
 */
function SparkEnvelopeMark({
  px,
  variant,
}: {
  px: number
  variant: 'default' | 'inverted'
}) {
  if (variant === 'inverted') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 128 128"
        width={px}
        height={px}
        role="img"
        aria-label={getCopy(R, 'purebox')}
        className="flex-shrink-0"
      >
        <defs>
          <linearGradient id="pbx-inv-tile" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#f2f4f6" />
          </linearGradient>
          <linearGradient id="pbx-inv-accent" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2563eb" />
            <stop offset="1" stopColor="#004ac6" />
          </linearGradient>
        </defs>
        <rect width="128" height="128" rx="30" fill="url(#pbx-inv-tile)" />
        <rect x="22" y="56" width="84" height="44" rx="10" fill="url(#pbx-inv-accent)" />
        <path
          d="M22 56 L64 78 L106 56"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.7"
          strokeWidth="4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d="M64 6 Q64 34 44 34 Q64 34 64 54 Q64 34 84 34 Q64 34 64 6 Z"
          fill="url(#pbx-inv-accent)"
        />
        <path
          d="M90 20 Q90 28 85 28 Q90 28 90 36 Q90 28 95 28 Q90 28 90 20 Z"
          fill="#2563eb"
          fillOpacity="0.4"
        />
      </svg>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 128 128"
      width={px}
      height={px}
      role="img"
      aria-label={getCopy(R, 'purebox2')}
      className="flex-shrink-0"
    >
      <defs>
        <linearGradient id="pbx-def-tile" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2563eb" />
          <stop offset="1" stopColor="#004ac6" />
        </linearGradient>
      </defs>
      <rect width="128" height="128" rx="30" fill="url(#pbx-def-tile)" />
      <rect x="22" y="56" width="84" height="44" rx="10" fill="#ffffff" />
      <path
        d="M22 56 L64 78 L106 56"
        fill="none"
        stroke="#004ac6"
        strokeOpacity="0.28"
        strokeWidth="4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M64 6 Q64 34 44 34 Q64 34 64 54 Q64 34 84 34 Q64 34 64 6 Z"
        fill="#ffffff"
      />
      <path
        d="M90 20 Q90 28 85 28 Q90 28 90 36 Q90 28 95 28 Q90 28 90 20 Z"
        fill="#ffffff"
        fillOpacity="0.5"
      />
    </svg>
  );
}

export function Logo({
  size = 'md',
  showText = true,
  animated = true,
  variant = 'default',
}: LogoProps) {
  const px = sizePx[size];

  return (
    <div className="flex items-center gap-2">
      <div
        className="relative flex-shrink-0"
        style={
          animated
            ? {
                animation: 'rotate-subtle 3s ease-in-out infinite',
                transformOrigin: 'center',
              }
            : undefined
        }
      >
        <SparkEnvelopeMark px={px} variant={variant} />
        {animated && (
          <div
            className="absolute inset-0 rounded-[22%] bg-blue-300 blur-2xl opacity-20 pointer-events-none"
            style={{ animation: 'pulse-subtle 2s ease-in-out infinite' }}
          />
        )}
      </div>
      {showText && (
        <span className={`${textSizes[size]} font-bold text-foreground`}>{getCopy(R, 'purebox3')}</span>
      )}
    </div>
  );
}
